import { useEffect, useState, type FormEvent } from 'react';
import { navigate, useSearch } from '../lib/router';
import { landingPage, verifyOtp } from '../stores/session';
import { mmss, useCountdown } from '../pages/account/kit';
import { errorMessage, Field, FormError, Heading, PrimaryButton } from './AuthForm';

/** Where to go after login: `?next=/path` when it's a same-origin path, otherwise the trade screen. */
export function useNext() {
  const next = new URLSearchParams(useSearch()).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

/** Second step of login (and of sign-up, which logs in right after registering): enter the emailed 6-digit code. */
export function OtpStep(props: {
  email: string;
  challengeId: string;
  /** 'totp' when the account uses an authenticator app (backup codes accepted too). */
  method: 'email' | 'totp';
  /** Echoed by the API only in dev when no SMTP server is configured. */
  devCode?: string;
  /** Seconds until Resend may send another code (email codes). */
  resendIn?: number;
  /** Sends a new code; returns the new wait before the next one, if any. */
  onResend: () => Promise<number | void>;
  onBack: () => void;
  /** Staff with Telegram too: switch this sign-in to a Telegram code. */
  onUseTelegram?: () => Promise<void>;
  title?: string;
  subtitle?: string;
  backLabel?: string;
  /** Where to go once the code is accepted (default: `?next=` or home). */
  next?: string;
}) {
  const fromQuery = useNext();
  const next = props.next ?? fromQuery;
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [wait, startWait] = useCountdown();
  useEffect(() => startWait(props.resendIn ?? 0), [props.resendIn, startWait]);
  const totp = props.method === 'totp';
  // Authenticator: 6 digits, or an 8-character backup code (XXXX-XXXX).
  const complete = totp ? code.replace(/-/g, '').length >= 6 : code.length === 6;
  const canSubmit = complete && !busy;

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const changePassword = await verifyOtp(props.challengeId, code);
      navigate(changePassword ?? landingPage(next), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    setNotice(null);
    try {
      const next = await props.onResend();
      if (next) startWait(next);
      setCode('');
      setNotice('We sent a new code.');
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <>
      {totp ? (
        <>
          <Heading title="Two-factor authentication" subtitle="Enter your app code" />
          <p className="mt-3.5 text-[15px] text-muted">
            Enter the 6-digit code from your authenticator app, or one of your backup codes.
          </p>
        </>
      ) : (
        <>
          <Heading title={props.title ?? 'Check your email'} subtitle={props.subtitle ?? 'Enter your code'} />
          <p className="mt-3.5 text-[15px] text-muted">
            We sent a 6-digit code to <span className="font-semibold text-fg">{props.email}</span>. It expires in 10
            minutes.
          </p>
        </>
      )}
      {props.devCode && (
        <p className="mt-4 rounded-2xl bg-accent-bg px-4 py-3 text-[13px] text-accent-fg">
          Dev mode (no SMTP configured): your code is <span className="num font-semibold">{props.devCode}</span>
        </p>
      )}

      <form onSubmit={submit} noValidate className="mt-8">
        <Field
          id="otp"
          label={totp ? 'Authenticator or backup code' : 'Verification code'}
          inputMode={totp ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          value={code}
          onChange={(v) => setCode(totp ? v.toUpperCase().replace(/[^0-9A-Z-]/g, '').slice(0, 9) : v.replace(/\D/g, '').slice(0, 6))}
          inputClassName="num tracking-[0.5em]"
          autoFocus
        />

        <div className="mx-1 mb-[22px] mt-1.5 flex justify-between text-sm">
          <button type="button" onClick={props.onBack} className="font-medium text-muted hover:text-fg">
            ← {props.backLabel ?? 'Use a different account'}
          </button>
          {!totp &&
            (wait > 0 ? (
              <span className="text-muted">
                Resend in <span className="num">{mmss(wait)}</span>
              </span>
            ) : (
              <button type="button" onClick={resend} className="font-semibold text-accent-fg">
                Resend code
              </button>
            ))}
        </div>

        {notice && !error && <p className="mb-3 px-1 text-sm text-up">{notice}</p>}
        <FormError message={error} />
        <PrimaryButton disabled={!canSubmit} busy={busy} busyLabel="Verifying…">
          Verify and continue
        </PrimaryButton>
        {props.onUseTelegram && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              props.onUseTelegram!().catch((err) => setError(errorMessage(err)));
            }}
            className="mt-4 w-full text-center text-sm font-semibold text-accent-fg"
          >
            Send a Telegram code instead
          </button>
        )}
      </form>
    </>
  );
}
